import type { ReactNode } from "react";

import type { CsvCell } from "@/lib/csv";

/** Values of one filter in the URL / API (`filter[<id>]=a&filter[<id>]=b`). */
export type FilterValue = string | string[];
export type FilterState = Readonly<Record<string, FilterValue>>;

export interface Option {
  value: string;
  label: string;
}

/** Filters a feature declares for its table (T11-05). */
export type FilterDef =
  | { type: "text"; id: string; label: string; placeholder?: string }
  | { type: "enum"; id: string; label: string; options: readonly Option[]; multiple?: boolean }
  | { type: "boolean"; id: string; label: string }
  /** Writes `filter[<id>_min]` / `filter[<id>_max]` as whole yen. */
  | { type: "money_range"; id: string; label: string }
  /** Writes `filter[<id>_from]` / `filter[<id>_to]` as JST dates (YYYY-MM-DD). */
  | { type: "date_range"; id: string; label: string }
  | { type: "location"; id: string; label: string; options: readonly Option[] };

export interface DataColumn<T> {
  id: string;
  /** Translated header. */
  header: string;
  cell: (row: T) => ReactNode;
  /** Server-side sort values for this column (money is a string — never sorted in the browser). */
  sort?: { asc: string; desc: string };
  align?: "start" | "end";
  /** Can be hidden in the column chooser (default true). */
  hideable?: boolean;
  defaultHidden?: boolean;
  /** Value written to the CSV (default: nothing → the column is left out of the file). */
  csv?: (row: T) => CsvCell;
  /** Money column: CSV header gets "(¥)" and the value must be the digits. */
  money?: boolean;
  /** Initial width in px. */
  size?: number;
}

export interface PageInfo {
  hasNextPage: boolean;
  nextCursor?: string | null;
  totalCount?: number | null;
}

export interface TablePage<T> {
  data: T[];
  pageInfo: PageInfo;
}

/** What the table asks the feature's list endpoint for (design/07 §1). */
export interface TableRequest {
  limit: number;
  cursor: string | null;
  sort: string | null;
  filters: FilterState;
  /** Visible column ids (for endpoints that accept `fields`). */
  fields: readonly string[];
}

export interface SavedView {
  id: string;
  name: string;
  filters: FilterState;
  sort: string | null;
  /** Column ids in order (visible ones); absent = the table default. */
  columns?: readonly string[];
  pinned?: boolean;
  /** Seeded by the feature — cannot be renamed or deleted. */
  system?: boolean;
}

export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
